/*
  GRAVITUS — configuratore «Trova il prodotto giusto» (giro 27/10/2026).
  Markup e testi in snippets/gravitus-configurator.liquid, dati dal vivo da
  templates/collection.finder.liquid (/collections/bilance?view=finder).

  - <dialog> nativa aperta con showModal() da [data-gconf-open] o da un link che
    finisce con #configuratore; con data-inline il configuratore sta in pagina.
  - Nessun passo: ogni risposta aggiorna subito i risultati. Per ogni bilancia si
    sceglie la versione (variante) che copre la portata richiesta con il minimo
    margine, con la divisione richiesta o migliore; il prezzo fa da spareggio.
  - «Pesi campione e taratura»: le domande diventano portata e divisione della
    bilancia da controllare; si propone il peso della classe giusta (E2 sotto
    0,01 g, F1 sotto 1 g, altrimenti M1) con la massa piu' vicina alla portata.
*/
(() => {
  if (customElements.get('gravitus-configurator')) return;

  const MAXS = [100, 200, 500, 1000, 2000, 3000, 6000, 15000, 30000, 60000, 150000, 300000, 600000, 1000000, 2000000, 3000000, 10000000, 15000000];
  const DS = [0.0001, 0.001, 0.005, 0.01, 0.05, 0.1, 0.5, 1, 2, 5, 10, 100, 1000, null];
  // riquadri «cosa pesi»: portata e divisione tipiche, sottocategorie (tag sub:) dove cercare
  const TILES = {
    oro: { max: 500, d: 0.01, omol: 'si', subs: ['gioielleria-oro-argento', 'gioielleria-pietre', 'gioielleria-laboratorio'] },
    lab: { max: 200, d: 0.001, subs: ['laboratorio-analitiche', 'laboratorio-precisione', 'laboratorio-scolastiche'] },
    umid: { max: 50, d: 0.001, subs: ['analizzatori-umidita'] },
    banco: { max: 15000, d: 5, omol: 'si', subs: ['commercio-peso-prezzo', 'commercio-scontrino'] },
    cucina: { max: 6000, d: 1, subs: ['ristorazione-cucina', 'ristorazione-inox', 'industria-compatte', 'laboratorio-precisione'] },
    pezzi: { max: 15000, d: 0.5, feat: 'conta', subs: ['industria-contapezzi', 'industria-compatte', 'industria-inox'] },
    pacchi: { max: 60000, d: null, subs: ['logistica-pesapacchi', 'industria-piattaforma', 'industria-inox'] },
    pallet: { max: 2000000, d: null, subs: ['logistica-piattaforme', 'logistica-transpallet', 'industria-piattaforma'] },
    gancio: { max: 300000, d: null, subs: ['industria-gancio', 'agricoltura-gancio', 'logistica-dinamometri'] },
    persone: { max: null, d: null, subs: [] },
    pesi: { max: 6000, d: 0.01, subs: [] },
  };

  const lang = () => document.documentElement.lang || 'it';
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const num = (n, max = 4) => Number(n).toLocaleString(lang(), { maximumFractionDigits: max });
  const fmtG = (g) => (g == null ? '—' : g >= 1e6 ? `${num(g / 1e6)} t` : g >= 1000 ? `${num(g / 1000)} kg` : `${num(g)} g`);
  const fmtD = (d) => (d == null || !d ? '—' : d < 0.001 ? `${num(d * 1000)} mg` : d >= 1000 ? `${num(d / 1000)} kg` : `${num(d)} g`);
  const money = (cents) => {
    try {
      return new Intl.NumberFormat(lang(), { style: 'currency', currency: 'EUR', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
    } catch (e) {
      return `€${(cents / 100).toFixed(2)}`;
    }
  };
  const nearestIdx = (arr, v) => {
    let b = 0;
    arr.forEach((x, i) => {
      if (x != null && Math.abs(Math.log(x) - Math.log(v)) < Math.abs(Math.log(arr[b]) - Math.log(v))) b = i;
    });
    return b;
  };
  // divisione stimata (3000 divisioni) per piattaforme e dinamometri senza dato
  const estD = (max) => {
    const raw = max / 3000;
    const e = Math.pow(10, Math.floor(Math.log10(raw)));
    for (const m of [1, 2, 5, 10]) if (m * e >= raw) return m * e;
    return raw;
  };

  class GravitusConfigurator extends HTMLElement {
    connectedCallback() {
      this.inline = this.hasAttribute('data-inline');
      this.dialog = this.querySelector(this.inline ? '.gconf' : 'dialog');
      if (!this.dialog) return;
      try {
        this.s = JSON.parse(this.querySelector('[data-gf-strings]').textContent);
      } catch (e) {
        this.s = {};
      }
      this.st = { tile: null, maxI: null, dI: 13, trade: '', feats: new Set(), view: 'cards', q: '' };
      this.$ = (sel) => this.querySelector(sel);
      this.bind();
      if (this.inline) {
        this.load();
        return;
      }
      this.$('[data-gconf-close]')?.addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (e) => e.target === this.dialog && this.close());
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('gconf-open'));
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-gconf-open], a[href$="#configuratore"]');
        if (!trigger) return;
        e.preventDefault();
        this.open(trigger);
      });
      if (window.location.hash === '#configuratore') this.open();
    }

    open(trigger) {
      this.trigger = trigger || null;
      // Il menu mobile di Dawn e' un <details>: va chiuso, o resta aperto sotto la finestra.
      document.querySelectorAll('.menu-drawer-container[open]').forEach((d) => d.querySelector('summary')?.click());
      document.documentElement.classList.add('gconf-open');
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      this.load();
      this.$('[data-gf-tile]')?.focus();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('gconf-open');
      this.trigger?.focus?.();
    }

    async load() {
      if (this.data || this.loading) return;
      this.loading = true;
      try {
        const r = await fetch(this.dataset.src, { credentials: 'same-origin' });
        this.data = await r.json();
        // varianti senza divisione: stimata dalla portata (segnata con ≈)
        this.data.p.forEach((p) => {
          p.v = p.v.map(([max, d, e, price, id, title, ok]) => {
            // peso-prezzo omologate: il titolo non ha la divisione, ma «e» si (e = d)
            if (!String(title).includes(' / ') && e) d = parseFloat(String(e).replace(',', '.')) || d;
            return { max, d: d || estD(max || 1), est: !d, e, price, id, title, ok };
          });
        });
        this.sync();
      } catch (e) {
        this.$('[data-gf-count]').textContent = this.s.loadError || '';
      } finally {
        this.loading = false;
      }
    }

    bind() {
      this.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-gf-tile]');
        if (tile) return this.pickTile(tile.dataset.gfTile);
        const trade = e.target.closest('[data-gf-trade]');
        if (trade) {
          this.st.trade = trade.dataset.gfTrade;
          return this.sync();
        }
        const feat = e.target.closest('[data-gf-feat]');
        if (feat) {
          const f = feat.dataset.gfFeat;
          this.st.feats.has(f) ? this.st.feats.delete(f) : this.st.feats.add(f);
          return this.sync();
        }
        const view = e.target.closest('[data-gf-view]');
        if (view) {
          this.st.view = view.dataset.gfView;
          return this.sync();
        }
        const fix = e.target.closest('[data-gf-fix]');
        if (fix) return this.fix(fix.dataset.gfFix);
        if (e.target.closest('[data-gf-table]')) {
          this.st.view = 'table';
          return this.sync();
        }
        if (e.target.closest('[data-gf-reset]')) {
          this.st = { tile: null, maxI: null, dI: 13, trade: '', feats: new Set(), view: 'cards', q: '' };
          this.$('[data-gf-q]').value = '';
          return this.sync();
        }
        if (e.target.closest('[data-gf-copy]')) this.copy(e.target.closest('[data-gf-copy]'));
      });
      this.$('[data-gf-max]').addEventListener('input', (e) => {
        this.st.maxI = Number(e.target.value);
        this.render();
      });
      this.$('[data-gf-d]').addEventListener('input', (e) => {
        this.st.dI = Number(e.target.value);
        this.render();
      });
      this.$('[data-gf-q]').addEventListener('input', (e) => {
        this.st.q = e.target.value.trim().toLowerCase();
        this.render();
      });
    }

    pickTile(k) {
      const st = this.st;
      if (st.tile === k) {
        st.tile = null;
      } else {
        const t = TILES[k];
        st.tile = k;
        st.maxI = t.max ? nearestIdx(MAXS, t.max) : null;
        st.dI = t.d ? nearestIdx(DS, t.d) : 13;
        if (t.omol) st.trade = t.omol;
        if (t.feat) st.feats.add(t.feat);
      }
      this.sync();
    }

    fix(k) {
      const st = this.st;
      if (k === 'feats') st.feats.clear();
      if (k === 'd') st.dI = Math.min(13, st.dI + 2);
      if (k === 'trade') st.trade = '';
      if (k === 'tile') st.tile = null;
      if (k === 'max') st.maxI = null;
      this.sync();
    }

    sync() {
      const st = this.st;
      this.querySelectorAll('[data-gf-tile]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gfTile === st.tile)));
      this.querySelectorAll('[data-gf-trade]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gfTrade === st.trade)));
      this.querySelectorAll('[data-gf-feat]').forEach((b) => b.setAttribute('aria-pressed', String(st.feats.has(b.dataset.gfFeat))));
      this.querySelectorAll('[data-gf-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gfView === st.view)));
      this.$('[data-gf-max]').value = st.maxI ?? 0;
      this.$('[data-gf-d]').value = st.dI;
      const weights = st.tile === 'pesi';
      this.querySelectorAll('[data-gf-scale-only]').forEach((el) => (el.hidden = weights));
      this.$('[data-gf-max-q]').textContent = weights ? this.s.weightsMax : this.s.qMax;
      this.$('[data-gf-d-q]').textContent = weights ? this.s.weightsD : this.s.qD;
      this.render();
    }

    /* ---------- motore ---------- */
    candidates() {
      const st = this.st;
      const tile = st.tile ? TILES[st.tile] : null;
      const needMax = st.maxI == null ? null : MAXS[st.maxI];
      const needD = DS[st.dI];
      const out = [];
      for (const p of this.data.p) {
        if (st.q && !p.n.toLowerCase().includes(st.q)) continue;
        if (tile && tile.subs.length && !p.s.some((x) => tile.subs.includes(x))) continue;
        if (st.trade === 'si' && p.o === 0) continue;
        if ([...st.feats].some((f) => !p.f.includes(f))) continue;
        let best = null;
        for (const v of p.v) {
          if (!v.ok) continue;
          if (needMax && v.max < needMax) continue;
          if (needD != null && v.d > needD * 1.0001) continue;
          // margine di portata vicino a 1, divisione vicina alla richiesta, prezzo come spareggio
          const head = needMax ? Math.log(v.max / needMax) : 0;
          const prec = needD != null ? Math.log(needD / v.d) * 0.6 : 0;
          const score = head + prec + (v.price ? v.price / 2e6 : 0.5);
          if (!best || score < best.score) best = { p, v, score };
        }
        if (best) out.push(best);
      }
      return out.sort((a, b) => a.score - b.score);
    }

    price(c) {
      const extra = this.st.trade === 'si' && c.p.o === 1 ? this.data.om : 0;
      return c.v.price ? c.v.price + extra : 0;
    }

    card(c, label) {
      const s = this.s;
      const omol = c.p.o === 2 ? `<span class="gfind__badge is-ok">${esc(s.omolOk)}</span>` : c.p.o === 1 ? `<span class="gfind__badge">${esc(s.omolCan)}</span>` : '';
      const extra = this.st.trade === 'si' && c.p.o === 1;
      const total = this.price(c);
      const price = total
        ? `<span class="gfind__price">${money(total)} <small>${esc(s.vat)}${extra ? ` · ${esc(s.inclOmol.replace('[price]', money(this.data.om)))}` : ''}</small></span>`
        : `<span class="gfind__price"><small>${esc(s.onRequest)}</small></span>`;
      const url = `${c.p.u}?variant=${c.v.id}`;
      const spec = `${fmtG(c.v.max)} · d ${c.v.est ? '≈ ' : ''}${fmtD(c.v.d)}${c.v.e ? ` · e ${esc(c.v.e)}` : ''}`;
      return `<article class="gfind__card${label === s.best ? ' is-top' : ''}">
        ${c.p.i ? `<img src="${esc(c.p.i)}" alt="" width="80" height="80" loading="lazy">` : '<span class="gfind__noimg"></span>'}
        <div class="gfind__cbody">
          <div class="gfind__badges">${label ? `<span class="gfind__badge${label === s.best ? ' is-best' : ''}">${esc(label)}</span>` : ''}${omol}</div>
          <h3 class="gfind__cname">${esc(c.p.n)}</h3>
          <p class="gfind__spec">${esc(s.version)} ${spec}</p>
          <div class="gfind__row">${price}
            <span class="gfind__btns">${c.p.q || !total ? `<a class="g-btn g-btn--secondary" href="${esc(this.dataset.quote)}">${esc(s.quote)}</a>` : ''}<a class="g-btn g-btn--primary" href="${esc(url)}">${esc(s.see)}</a></span>
          </div>
        </div>
      </article>`;
    }

    weights() {
      // portata e divisione della bilancia da controllare -> classe e massa del peso
      const s = this.s;
      const cap = this.st.maxI == null ? null : MAXS[this.st.maxI];
      const d = DS[this.st.dI];
      const cls = d == null ? null : d < 0.01 ? 'E2' : d < 1 ? 'F1' : 'M1';
      const list = this.data.w.filter((w) => !cls || (w.c || '').split(',').includes(cls));
      const rows = [];
      list.forEach((w) => {
        let best = null;
        w.v.forEach(([g, price, id, title]) => {
          if (cap && g > cap) return;
          if (!best || g > best.g) best = { g, price, id, title };
        });
        if (best) rows.push({ w, best });
      });
      const why = cls ? s.weightsWhy.replace('[class]', cls).replace('[d]', fmtD(d)) : '';
      const html = rows
        .slice(0, 6)
        .map(
          ({ w, best }) => `<article class="gfind__card">
          ${w.i ? `<img src="${esc(w.i)}" alt="" width="80" height="80" loading="lazy">` : '<span class="gfind__noimg"></span>'}
          <div class="gfind__cbody">
            <h3 class="gfind__cname">${esc(w.n)}</h3>
            <p class="gfind__spec">${esc(s.version)} ${esc(best.title)}</p>
            <div class="gfind__row"><span class="gfind__price">${money(best.price)} <small>${esc(s.vat)}</small></span>
              <span class="gfind__btns"><a class="g-btn g-btn--primary" href="${esc(w.u)}?variant=${best.id}">${esc(s.see)}</a></span></div>
          </div></article>`
        )
        .join('');
      return {
        n: rows.length,
        html: `${why ? `<p class="gfind__note">${esc(why)}</p>` : ''}<div class="gfind__cards">${html}</div>
          <p class="gfind__note"><a href="${esc(this.dataset.cert)}">${esc(s.weightsCert)}</a></p>`,
      };
    }

    render() {
      const st = this.st;
      const s = this.s;
      const needMax = st.maxI == null ? null : MAXS[st.maxI];
      const needD = DS[st.dI];
      const weights = st.tile === 'pesi';
      this.$('[data-gf-max-val]').textContent = needMax ? (s.maxVal || '[v]').replace('[v]', fmtG(needMax)) : '—';
      this.$('[data-gf-max-ex]').textContent = needMax ? s.maxEx : s.maxExNone;
      this.$('[data-gf-d-val]').textContent = needD == null ? s.dDontKnow : (s.dVal || '[v]').replace('[v]', fmtD(needD));
      this.$('[data-gf-d-ex]').textContent = needD == null ? s.dExNone : (s.dex || [])[st.dI] || '';
      this.$('[data-gf-trade-note]').textContent = st.trade === 'si' ? s.tradeSi.replace('[price]', this.data ? money(this.data.om) : '') : st.trade === 'no' ? s.tradeNo : s.tradeUnknown;
      if (!this.data) return;

      const out = this.$('[data-gf-out]');
      let n = 0;
      let html = '';
      let list = [];
      if (st.tile === 'persone') {
        html = `<div class="gfind__empty"><p>${esc(s.persone)}</p><a class="g-btn g-btn--primary" href="${esc(this.dataset.medicale || this.dataset.quote)}">${esc(s.personeCta)}</a></div>`;
      } else if (weights) {
        const r = this.weights();
        n = r.n;
        html = r.html;
      } else {
        list = this.candidates();
        n = list.length;
        if (!n) {
          const fix = [];
          if (st.feats.size) fix.push(`<button type="button" class="gfind__chip" data-gf-fix="feats">${esc(s.fixFeats)}</button>`);
          if (needD != null) fix.push(`<button type="button" class="gfind__chip" data-gf-fix="d">${esc(s.fixD)}</button>`);
          if (st.trade === 'si') fix.push(`<button type="button" class="gfind__chip" data-gf-fix="trade">${esc(s.fixTrade)}</button>`);
          if (st.tile) fix.push(`<button type="button" class="gfind__chip" data-gf-fix="tile">${esc(s.fixTile)}</button>`);
          if (needMax) fix.push(`<button type="button" class="gfind__chip" data-gf-fix="max">${esc(s.fixMax)}</button>`);
          html = `<div class="gfind__empty"><strong>${esc(s.emptyTitle)}</strong><p>${esc(s.emptyText)}</p><div class="gfind__chips">${fix.join('')}</div></div>`;
        } else if (st.view === 'table') {
          const rows = [...list]
            .sort((a, b) => a.v.d - b.v.d || a.v.max - b.v.max)
            .map((c) => {
              const om = c.p.o === 2 ? s.omolOk : c.p.o === 1 ? s.omolCan : s.internal;
              const total = this.price(c);
              return `<tr><td><a href="${esc(c.p.u)}?variant=${c.v.id}">${esc(c.p.n)}</a></td><td class="num">${fmtG(c.v.max)}</td><td class="num">${c.v.est ? '≈ ' : ''}${fmtD(c.v.d)}</td><td class="num">${esc(c.v.e || '—')}</td><td>${esc(om)}</td><td class="num">${total ? money(total) : esc(s.onRequest)}</td></tr>`;
            })
            .join('');
          html = `<div class="gfind__table"><table><thead><tr><th>${esc(s.colModel)}</th><th>${esc(s.colMax)}</th><th>${esc(s.colD)}</th><th>e</th><th>${esc(s.colOmol)}</th><th>${esc(s.colPrice)}</th></tr></thead><tbody>${rows}</tbody></table></div>`;
        } else {
          const priced = list.filter((c) => this.price(c));
          const top = list[0];
          const cheap = priced.length ? priced.reduce((a, b) => (this.price(a) <= this.price(b) ? a : b)) : null;
          const precise = list.reduce((a, b) => (a.v.d < b.v.d || (a.v.d === b.v.d && (this.price(a) || 1e12) < (this.price(b) || 1e12)) ? a : b));
          const shown = new Set();
          const parts = [];
          const add = (c, label) => {
            if (c && !shown.has(c.p.u)) {
              shown.add(c.p.u);
              parts.push(this.card(c, label));
            }
          };
          add(top, s.best);
          add(cheap, s.cheap);
          add(precise, s.precise);
          const rest = list.filter((c) => !shown.has(c.p.u));
          rest.slice(0, 3).forEach((c) => add(c, ''));
          const more = n - shown.size;
          html = `<div class="gfind__cards">${parts.join('')}</div>${more > 0 ? `<button type="button" class="gfind__link" data-gf-table>${esc(s.more.replace('[n]', n))}</button>` : ''}`;
        }
      }
      out.innerHTML = html;
      const countTxt = weights
        ? s.weightsCount.replace('[n]', n)
        : st.tile === 'persone'
        ? ''
        : n === 0
        ? s.countNone
        : n === 1
        ? s.countOne
        : s.countOther.replace('[n]', n);
      this.$('[data-gf-count]').innerHTML = `${esc(countTxt)}${n && !weights ? `<span>${esc(s.countSub)}</span>` : ''}`;
      this.$('[data-gf-mcount]').textContent = countTxt;
      this.whatsapp(list[0]);
    }

    summary(top) {
      const st = this.st;
      const s = this.s;
      const lines = [s.waIntro];
      if (st.tile) lines.push(`${s.waWhat} ${s.tiles[st.tile] || st.tile}`);
      if (st.maxI != null) lines.push(`${s.waMax} ${fmtG(MAXS[st.maxI])}`);
      lines.push(`${s.waD} ${DS[st.dI] == null ? s.dDontKnow : fmtD(DS[st.dI])}`);
      if (st.tile !== 'pesi') lines.push(`${s.waTrade} ${st.trade === 'si' ? s.tradeYes : st.trade === 'no' ? s.tradeNo2 : s.dDontKnow}`);
      if (st.feats.size) lines.push(`${s.waFeats} ${[...st.feats].map((f) => s.feats[f]).join(', ')}`);
      if (top) lines.push(`${s.waPick} ${top.p.n} · ${fmtG(top.v.max)} / ${fmtD(top.v.d)}`);
      return lines.join('\n');
    }

    whatsapp(top) {
      const wa = this.$('[data-gf-wa]');
      this.text = this.summary(top);
      if (!this.dataset.wa) {
        wa.hidden = true;
        return;
      }
      wa.href = `${this.dataset.wa}?text=${encodeURIComponent(this.text)}`;
    }

    copy(btn) {
      const done = () => (btn.textContent = btn.dataset.done);
      if (navigator.clipboard) navigator.clipboard.writeText(this.text || '').then(done, () => {});
    }
  }

  customElements.define('gravitus-configurator', GravitusConfigurator);
})();
